import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, HeartHandshake, Leaf, Scissors, Shirt, Sparkles, Star } from "lucide-react";
import homebanner from "../assets/images/homebanner.webp";
import offer1 from "../assets/images/offer1.webp";
import offer3 from "../assets/images/offer3.webp";

const values = [
  {
    icon: <Shirt size={22} />,
    title: "Sarees With Soul",
    text: "Every saree is chosen for its fall, texture, border work, and the quiet details that make a drape feel deeply personal.",
  },
  {
    icon: <Leaf size={22} />,
    title: "Indian At Heart",
    text: "We celebrate the timeless beauty of Indian sarees through festive colors, graceful motifs, and styling made for today’s women.",
  },
  {
    icon: <HeartHandshake size={22} />,
    title: "For Every Occasion",
    text: "From temple visits and family gatherings to weddings and festive evenings, our sarees are curated to feel elegant and comfortable.",
  },
];

const processSteps = [
  "Discover saree stories through fabric, weave, color, and occasion.",
  "Curate Indian-style sarees for comfort, drape, border, and blouse-pairing potential.",
  "Present each saree with styling context so you can shop with confidence.",
  "Pack every order with care, so your saree arrives celebration-ready.",
];

const AboutPage = () => {
  return (
    <main className="about-page">
      <section className="about-page-hero">
        <div className="about-page-hero-pattern" />
        <div className="about-page-hero-content">
          <p className="about-page-eyebrow">
            <Sparkles size={16} />
            Tanuraaga Weaves
          </p>
          <h1>Indian sarees for women who love graceful tradition.</h1>
          <p className="about-page-hero-text">
            Tanuraaga is dedicated to women’s sarees in Indian style, bringing together elegant drapes for festivals, family occasions, weddings, rituals, and everyday moments of grace.
          </p>
          <div className="about-page-hero-actions">
            <Link className="about-page-primary-btn" to="/">
              Explore Collection <ArrowRight size={18} />
            </Link>
            <Link className="about-page-secondary-btn" to="/contact">
              Talk To Us
            </Link>
          </div>
        </div>
        <div className="about-page-hero-visual">
          <div className="about-page-image-card about-page-image-card-main">
            <img src={homebanner} alt="Elegant Indian saree styling" />
          </div>
          <div className="about-page-floating-card">
            <Star size={18} />
            <span>Curated sarees for celebrations, rituals, weddings, and everyday grace.</span>
          </div>
        </div>
      </section>

      <section className="about-page-story">
        <div className="about-page-story-media">
          <img src={offer1} alt="Traditional saree textile inspiration" />
          <img src={offer3} alt="Festive Indian saree collection" />
        </div>
        <div className="about-page-story-copy">
          <p className="about-page-section-kicker">Our Story</p>
          <h2>Born from the love of sarees, soft drapes, and timeless Indian dressing.</h2>
          <p>
            Tanuraaga was created for women who see a saree as more than an outfit. It is memory, identity, celebration, and quiet confidence woven into six yards.
          </p>
          <p>
            We focus on curated saree collections rather than endless clutter, helping you find drapes that match your occasion, your personality, and the way you carry Indian elegance.
          </p>
        </div>
      </section>

      <section className="about-page-values">
        {values.map((item) => (
          <article className="about-page-value-card" key={item.title}>
            <div className="about-page-value-icon">{item.icon}</div>
            <h3>{item.title}</h3>
            <p>{item.text}</p>
          </article>
        ))}
      </section>

      <section className="about-page-process">
        <div className="about-page-process-heading">
          <p className="about-page-section-kicker">How We Curate</p>
          <h2>From fabric inspiration to sarees ready for your special moments.</h2>
        </div>
        <div className="about-page-process-list">
          {processSteps.map((step, index) => (
            <div className="about-page-process-item" key={step}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <p>{step}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="about-page-cta">
        <Scissors size={28} />
        <h2>Ready to find your next beautiful saree?</h2>
        <p>Browse Indian-style sarees selected for drape, comfort, occasion, and a little everyday magic.</p>
        <Link className="about-page-primary-btn" to="/">
          Start Shopping <ArrowRight size={18} />
        </Link>
      </section>
    </main>
  );
};

export default AboutPage;
